import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, BookOpen, Clock, TrendingUp } from 'lucide-react';
import api from '../api';



function AnimatedCounter({ target, duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    started.current = false; setCount(0);
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        let s = 0; const step = target / (duration * 60);
        const t = setInterval(() => { s += step; if (s >= target) { setCount(target); clearInterval(t); } else setCount(Math.floor(s)); }, 1000/60);
      }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{count.toLocaleString()}</span>;
}

function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`transition-all duration-700 ${className}`}
    style={{ opacity: v?1:0, transform: v?'translateY(0)':'translateY(30px)', transitionDelay: `${delay}ms` }}>{children}</div>;
}

export default function FeaturedStories() {
  const [stories, setStories] = useState([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    api.get(`/api/stories`).then(r => setStories(r.data)).catch(() => {});
  }, []);

  if (stories.length === 0) return null;

  const story = stories[current];
  const progressPercent = Math.min((story.total_episodes / story.target_episodes) * 100, 100);
  const goPrev = () => setCurrent((current - 1 + stories.length) % stories.length);
  const goNext = () => setCurrent((current + 1) % stories.length);

  return (
    <section id="episodes" data-testid="featured-stories-section" className="py-20 sm:py-28 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neonBlue/[0.02] to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <FadeIn className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-neonGold border border-neonGold/30 rounded-full mb-4 bg-neonGold/5">
            Featured Stories
          </span>
          {/* Story name with FIXED position arrows */}
          <div className="relative flex items-center justify-center h-16">
            {stories.length > 1 && (
              <button onClick={goPrev} data-testid="story-prev-btn"
                className="absolute left-0 sm:left-[15%] w-12 h-12 rounded-full border border-neonBlue/30 flex items-center justify-center text-neonBlue hover:bg-neonBlue/10 hover:border-neonBlue transition-all">
                <ChevronLeft size={24} />
              </button>
            )}
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white px-16">
              {story.story_name}
            </h2>
            {stories.length > 1 && (
              <button onClick={goNext} data-testid="story-next-btn"
                className="absolute right-0 sm:right-[15%] w-12 h-12 rounded-full border border-neonBlue/30 flex items-center justify-center text-neonBlue hover:bg-neonBlue/10 hover:border-neonBlue transition-all">
                <ChevronRight size={24} />
              </button>
            )}
          </div>
          {stories.length > 1 && (
            <div className="flex justify-center gap-2 mt-4" data-testid="story-dots">
              {stories.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)} data-testid={`story-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-neonBlue' : 'w-2 bg-white/20 hover:bg-white/40'}`} />
              ))}
            </div>
          )}
        </FadeIn>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Image - FIXED 16:9 aspect ratio, trimmed height */}
          <FadeIn delay={100}>
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-br from-neonBlue/20 via-transparent to-neonGold/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-[16/10]">
                <img
                  src={story.image_url}
                  alt={story.story_name}
                  data-testid="story-image"
                  className="w-full h-full object-cover object-top"
                />
                <div className={`absolute top-4 right-4 font-bold text-xs px-3 py-1.5 rounded-full animate-glow-pulse ${
                  story.status === 'New' ? 'bg-neonGold/90 text-black' : story.status === 'Completed' ? 'bg-green-500/90 text-black' : 'bg-neonBlue/90 text-black'
                }`}>
                  {story.status}
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Info */}
          <FadeIn delay={200} className="space-y-6">
            <p className="text-gray-400 text-lg leading-relaxed">{story.description}</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="glass-card rounded-xl p-4 text-center" data-testid="stat-total-episodes">
                <BookOpen className="w-5 h-5 text-neonBlue mx-auto mb-2" />
                <div className="font-heading text-2xl sm:text-3xl font-bold text-white">
                  <AnimatedCounter key={`total-${current}`} target={story.total_episodes} />
                </div>
                <div className="text-xs text-gray-500 mt-1">Audio Episodes</div>
              </div>
              <div className="glass-card rounded-xl p-4 text-center" data-testid="stat-daily-release">
                <Clock className="w-5 h-5 text-neonGold mx-auto mb-2" />
                <div className="font-heading text-2xl sm:text-3xl font-bold text-white">{story.daily_release}</div>
                <div className="text-xs text-gray-500 mt-1">Daily Release</div>
              </div>
              <div className="glass-card rounded-xl p-4 text-center" data-testid="stat-status">
                <TrendingUp className="w-5 h-5 text-green-400 mx-auto mb-2" />
                <div className="font-heading text-base font-bold text-green-400">{story.status}</div>
                <div className="text-xs text-gray-500 mt-1">Status</div>
              </div>
            </div>
            <div data-testid="episode-progress-bar">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Episode Progress</span>
                <span className="text-neonBlue font-mono">{story.total_episodes} / {story.target_episodes}</span>
              </div>
              <div className="h-3 w-full bg-gray-800/50 rounded-full overflow-hidden border border-white/5">
                <div
                  key={`progress-${current}`}
                  className="h-full progress-fill rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
            {story.telegram_link && (
              <a href={story.telegram_link} target="_blank" rel="noopener noreferrer" data-testid="story-telegram-btn"
                className="inline-flex items-center gap-2 bg-neonBlue text-black font-bold px-8 py-3.5 rounded-full hover:bg-neonBlue/80 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-all text-sm">
                Read Latest on Telegram
              </a>
            )}
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
